import { useCallback, useMemo, useState } from "react";
import {
  AcademyGroupingVariant,
  SwimmingExerciseGroup,
  SwimmingFilters,
  SwimmingInstructor,
  SwimmingLesson,
  SwimmingScheduleData,
} from "../../models/SwimmingScheduleData";
import {
  getDayColumns,
  getLessonDateKey,
  isBeforeNow,
  isDateInSaleStepRange,
} from "./swimmingDateUtils";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** "0,1;0,2" → ["0,1", "0,2"] */
const parseIdList = (raw?: string): string[] => (raw ? raw.split(";").filter(Boolean) : []);

/** "0,1" → objectDb=0, objectId=1 */
const matchesId = (id: string, objectDb: number, objectId: number): boolean => {
  const [db, obj] = id.split(",").map(Number);
  return db === objectDb && obj === objectId;
};

const parseHour = (timeStr: string): string => timeStr.slice(0, 2) + ":00";

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseSwimmingLessonsOptions {
  data: SwimmingScheduleData | null;
  anchorDate: Date;
  onDateChange: (date: Date) => void;
  isWeekly?: boolean;
  isMobile?: boolean;
  minDate?: Date;
  maxDate?: Date;
  defaultCourseType?: string;
  defaultExerciseGroup?: string;
  defaultInstructor?: string;
  defaultGroupingVariant?: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export default function useSwimmingLessons({
  data,
  anchorDate,
  onDateChange,
  isWeekly = false,
  isMobile = false,
  minDate,
  maxDate,
  defaultCourseType,
  defaultExerciseGroup,
  defaultInstructor,
  defaultGroupingVariant,
}: UseSwimmingLessonsOptions) {
  const resolvedVariant = (defaultGroupingVariant as AcademyGroupingVariant) || AcademyGroupingVariant.ByLanes;

  const [filters, setFilters] = useState<SwimmingFilters>({
    onlyAvailable: false,
    instructor: defaultInstructor || "",
    exerciseGroups: parseIdList(defaultExerciseGroup),
    courseType: (defaultCourseType as SwimmingFilters["courseType"]) || "ALL",
    locations: [],
    groupingVariant: resolvedVariant,
  });

  const [selectedLesson, setSelectedLesson] = useState<SwimmingLesson | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showPastHours, setShowPastHours] = useState(false);

  const lessons = useMemo(() => data?.lessons ?? [], [data]);
  const locations = useMemo(() => data?.locations ?? [], [data]);
  const instructors: SwimmingInstructor[] = useMemo(() => data?.trainers ?? [], [data]);
  const exerciseGroups: SwimmingExerciseGroup[] = useMemo(() => data?.groups ?? [], [data]);

  const dayColumns = useMemo(
    () => (isWeekly ? getDayColumns(anchorDate, isWeekly, isMobile) : []),
    [anchorDate, isWeekly, isMobile],
  );

  // Apply filters
  const filteredLessons = useMemo(() => {
    return lessons.filter((lesson) => {
      if (isWeekly) {
        const lessonDate = new Date(lesson.startTime);
        if (!isDateInSaleStepRange(lessonDate, minDate, maxDate)) return false;
      }

      if (filters.onlyAvailable && lesson.reserved >= lesson.capacity) return false;

      if (
        filters.exerciseGroups.length > 0 &&
        !filters.exerciseGroups.some((id) =>
          matchesId(id, lesson.exerciseGroup?.objectDb ?? -1, lesson.exerciseGroup?.objectId ?? -1),
        )
      )
        return false;

      if (
        filters.instructor &&
        !matchesId(filters.instructor, lesson.human?.objectDb ?? -1, lesson.human?.objectId ?? -1)
      )
        return false;

      if (filters.courseType !== "ALL" && lesson.courseType !== filters.courseType) return false;

      if (filters.locations.length > 0 && !filters.locations.includes(lesson.location)) return false;

      return true;
    });
  }, [lessons, filters, isWeekly, minDate, maxDate]);

  // Active columns — depend on grouping variant (daily) or day keys (weekly)
  const activeColumns = useMemo((): string[] => {
    if (isWeekly) {
      return dayColumns;
    }

    const variant = filters.groupingVariant;

    if (variant === AcademyGroupingVariant.ByAssortmentGroups) {
      const groupIdsInData = new Set(
        filteredLessons.map((l) => `${l.exerciseGroup?.objectDb ?? -1},${l.exerciseGroup?.objectId ?? -1}`),
      );
      return exerciseGroups.filter((g) => groupIdsInData.has(`${g.objectDb},${g.objectId}`)).map((g) => g.displayName);
    }

    if (variant === AcademyGroupingVariant.ByInstructors) {
      const instrIdsInData = new Set(
        filteredLessons.map((l) => `${l.human?.objectDb ?? -1},${l.human?.objectId ?? -1}`),
      );
      return instructors
        .filter((i) => instrIdsInData.has(`${i.objectDb},${i.objectId}`))
        .map((i) => `${i.name} ${i.lastName}`.trim());
    }

    // ByLanes (default)
    const locationsInData = new Set(filteredLessons.map((l) => l.location));
    const base = filters.locations.length > 0 ? filters.locations : locations;
    return base.filter((loc) => locationsInData.has(loc));
  }, [filteredLessons, filters, locations, instructors, exerciseGroups, isWeekly, dayColumns]);

  const isDayColumnInRange = useCallback(
    (dayKey: string): boolean => {
      const date = new Date(dayKey + "T00:00:00");
      return isDateInSaleStepRange(date, minDate, maxDate);
    },
    [minDate, maxDate],
  );

  // Unique hours from filtered lessons
  const allHours = useMemo(() => {
    const hours = Array.from(new Set(filteredLessons.map((l) => parseHour(l.startTime_str))));
    return hours.sort();
  }, [filteredLessons]);

  const hasPastHours = useMemo(() => {
    if (showPastHours) return false;

    if (isWeekly) {
      return activeColumns.some((dayKey) => {
        if (!isDayColumnInRange(dayKey)) return false;
        const columnDate = new Date(dayKey + "T00:00:00");
        return allHours.some((h) => isBeforeNow(h, columnDate));
      });
    }

    return allHours.some((h) => isBeforeNow(h, anchorDate));
  }, [allHours, showPastHours, anchorDate, isWeekly, activeColumns, isDayColumnInRange]);

  const visibleHours = useMemo(() => {
    if (showPastHours) return allHours;

    if (isWeekly) {
      return allHours.filter((hour) =>
        activeColumns.some((dayKey) => {
          if (!isDayColumnInRange(dayKey)) return false;
          const columnDate = new Date(dayKey + "T00:00:00");
          return !isBeforeNow(hour, columnDate);
        }),
      );
    }

    return allHours.filter((h) => !isBeforeNow(h, anchorDate));
  }, [allHours, showPastHours, anchorDate, isWeekly, activeColumns, isDayColumnInRange]);

  const findLessonsForSlot = (column: string, hour: string): SwimmingLesson[] => {
    if (isWeekly) {
      if (!isDayColumnInRange(column)) return [];
      return filteredLessons.filter(
        (lesson) =>
          getLessonDateKey(lesson) === column && lesson.startTime_str.startsWith(hour.slice(0, 2)),
      );
    }

    return filteredLessons.filter((lesson) => {
      if (!lesson.startTime_str.startsWith(hour.slice(0, 2))) return false;
      if (filters.groupingVariant === AcademyGroupingVariant.ByAssortmentGroups)
        return lesson.exerciseGroupName === column;
      if (filters.groupingVariant === AcademyGroupingVariant.ByInstructors)
        return `${lesson.humanName ?? ""} ${lesson.humanLastName ?? ""}`.trim() === column;
      return lesson.location === column;
    });
  };

  const handleLessonClick = (lesson: SwimmingLesson) => {
    setSelectedLesson(lesson);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      onlyAvailable: false,
      instructor: "",
      exerciseGroups: [],
      courseType: "ALL",
      locations: [],
      groupingVariant: resolvedVariant,
    });
  };

  const handleDateChange = (date: Date) => {
    onDateChange(date);
    setShowPastHours(false);
  };

  return {
    filters,
    setFilters,
    selectedLesson,
    modalOpen,
    handleLessonClick,
    handleCloseModal,
    handleClearFilters,
    handleDateChange,
    showPastHours,
    setShowPastHours,
    visibleHours,
    hasPastHours,
    filteredLessons,
    activeColumns,
    findLessonsForSlot,
    isDayColumnInRange,
    locations,
    instructors,
    exerciseGroups,
  };
}
